import "./LoginPOS.css";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, savePosSession } from "./api";

function errorText(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible conectar con el servidor.";
}

// Los archivos públicos se resuelven desde el servidor del backend.
// Por ejemplo: /uploads/logos/empresa.png.
function getPublicAssetUrl(path) {
  if (typeof path !== "string" || !path.trim()) {
    return "";
  }

  try {
    const backendUrl = new URL(
      api.defaults.baseURL || "/",
      window.location.origin
    );

    const assetUrl = new URL(path.trim(), `${backendUrl.origin}/`);

    if (!["http:", "https:"].includes(assetUrl.protocol)) {
      return "";
    }

    return assetUrl.href;
  } catch {
    return "";
  }
}

// Evita usar valores vacíos o variables CSS sin resolver
// que puedan llegar como valores predeterminados del backend.
function getBrandColor(value, fallback) {
  if (
    typeof value === "string" &&
    value.trim() &&
    !value.includes("var(") &&
    CSS.supports("color", value.trim())
  ) {
    return value.trim();
  }

  return fallback;
}

export default function LoginPOS() {
  const { company } = useParams();
  const navigate = useNavigate();

  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyError, setCompanyError] = useState("");
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setCompanyData(null);
    setCompanyError("");
    setError("");
    setLogoFailed(false);
    setPassword("");
    setShowPassword(false);

    if (!company) {
      setCompanyError("No se ha indicado una empresa.");
      setLoading(false);
      return () => controller.abort();
    }

    api
      .get(
        `/companies/by-subdomain/${encodeURIComponent(company)}/branding`,
        { signal: controller.signal }
      )
      .then((response) => {
        if (!controller.signal.aborted) {
          setCompanyData(response.data);
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setCompanyError(errorText(requestError));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [company]);

  const name =
    companyData?.commercial_name ||
    companyData?.company_name ||
    company ||
    "Empresa";

  const primaryColor = getBrandColor(
    companyData?.primary_color,
    "#1f5d7a"
  );

  const secondaryColor = getBrandColor(
    companyData?.secondary_color,
    primaryColor
  );

  const backgroundColor = getBrandColor(
    companyData?.login_background_color,
    "#eef2ff"
  );

  const logoUrl = getPublicAssetUrl(companyData?.logo);
  const faviconUrl = getPublicAssetUrl(companyData?.favicon);

  useEffect(() => {
    if (!companyData) return undefined;

    const previousTitle = document.title;
    document.title = `${name} | NetLedger POS`;

    let favicon = document.querySelector("link[rel~='icon']");
    const previousHref = favicon?.getAttribute("href");
    let createdFavicon = false;

    if (faviconUrl) {
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
        createdFavicon = true;
      }

      favicon.href = faviconUrl;
    }

    return () => {
      document.title = previousTitle;

      if (faviconUrl && favicon) {
        if (createdFavicon) {
          favicon.remove();
        } else if (previousHref !== null) {
          favicon.setAttribute("href", previousHref);
        } else {
          favicon.removeAttribute("href");
        }
      }
    };
  }, [companyData, name, faviconUrl]);

  async function handleLogin(event) {
    event.preventDefault();

    if (saving || !companyData || companyError) return;

    setSaving(true);
    setError("");

    try {
      const response = await api.post("/auth/login-pos", {
        email: email.trim(),
        password,
        subdomain: company,
      });

      if (
        response.data.app !== "pos" ||
        response.data.subdomain !== company ||
        !response.data.access_token
      ) {
        throw new Error("Respuesta de autenticación inválida.");
      }

      savePosSession(company, response.data);

      navigate(`/${encodeURIComponent(company)}/ventas`, {
        replace: true,
      });
    } catch (loginError) {
      setError(
        loginError.response
          ? errorText(loginError)
          : loginError.message || "No fue posible iniciar sesión."
      );
    } finally {
      setSaving(false);
    }
  }

  const pageStyle = {
    "--pos-primary": primaryColor,
    "--pos-secondary": secondaryColor,
    "--pos-background": backgroundColor,
  };

  if (loading) {
    return (
      <main className="pos-login-page" style={pageStyle}>
        <section
          className="pos-login-status"
          role="status"
          aria-live="polite"
        >
          <span className="pos-login-spinner" aria-hidden="true" />
          <h1>Cargando empresa</h1>
          <p>Preparando tu punto de venta…</p>
        </section>
      </main>
    );
  }

  if (companyError) {
    return (
      <main className="pos-login-page" style={pageStyle}>
        <section className="pos-login-status">
          <span className="pos-login-eyebrow">NETLEDGER POS</span>
          <h1>No pudimos cargar la empresa</h1>
          <p role="alert">{companyError}</p>

          <button
            type="button"
            className="pos-login-submit"
            onClick={() => window.location.reload()}
          >
            Reintentar
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="pos-login-page" style={pageStyle}>
      <section
        className="pos-login-container"
        aria-labelledby="pos-login-title"
      >
        <div className="pos-login-form-panel">
          <form
            className="pos-login-form"
            onSubmit={handleLogin}
            aria-busy={saving}
          >
            <header className="pos-login-header">
              <span className="pos-login-eyebrow">NETLEDGER POS</span>

              <h1 id="pos-login-title">{name}</h1>

              <p>Inicia sesión para acceder a tu punto de venta.</p>
            </header>

            <div className="pos-login-field">
              <input
                id="pos-email"
                name="email"
                type="email"
                placeholder=" "
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                disabled={saving}
              />

              <label htmlFor="pos-email">Correo electrónico</label>
            </div>

            <div className="pos-login-field pos-login-password-field">
              <input
                id="pos-password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder=" "
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                disabled={saving}
              />

              <label htmlFor="pos-password">Contraseña</label>

              <button
                className="pos-login-password-toggle"
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
                aria-pressed={showPassword}
                aria-controls="pos-password"
                disabled={saving}
              >
                {showPassword ? "Ocultar" : "Ver"}
              </button>
            </div>

            {error && (
              <div className="pos-login-error" role="alert">
                {error}
              </div>
            )}

            <button
              className="pos-login-submit"
              disabled={saving}
              type="submit"
            >
              {saving && (
                <span
                  className="pos-login-spinner pos-login-spinner-small"
                  aria-hidden="true"
                />
              )}

              {saving ? "Ingresando…" : "Ingresar al POS"}
            </button>

            <p className="pos-login-form-note">
              Acceso para usuarios autorizados de {name}.
            </p>
          </form>
        </div>

        <aside className="pos-login-brand-panel">
          <div className="pos-login-brand">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt={`Logo de ${name}`}
                className="pos-login-logo"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="pos-login-logo-placeholder" aria-hidden="true">
                {name.charAt(0).toUpperCase()}
              </div>
            )}

            <div className="pos-login-brand-copy">
              <span className="pos-login-brand-label">PUNTO DE VENTA</span>
              <h2>Bienvenido a tu POS</h2>
              <p>
                Gestiona tus ventas y atiende a tus clientes desde un solo
                lugar.
              </p>
            </div>
          </div>
        </aside>
      </section>

      <footer className="pos-login-footer">
        NetLedger POS · SYNTRIXA S.A.S.
      </footer>
    </main>
  );
}